/**
 * Alert component with Dashboard3 styling
 *
 * @example
 * ```tsx
 * <Alert status="success">Operation successful!</Alert>
 * <Alert status="error">Something went wrong</Alert>
 * ```
 */

"use client";

import {
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Alert as ChakraAlert,
  type AlertProps as ChakraAlertProps,
} from "@chakra-ui/react";
import { useThemeTokens, useInteractiveTokens } from "@/lib/theme/tokens";

export interface AlertProps extends ChakraAlertProps {
  /**
   * Alert status
   */
  status?: "success" | "error" | "warning" | "info";
}

export function Alert({ status = "info", children, ...props }: AlertProps) {
  const theme = useThemeTokens();
  const interactive = useInteractiveTokens();

  const statusColors = {
    success: {
      bg: theme.accent.mutedBg,
      color: theme.accent.muted,
      borderColor: theme.accent.mutedBorder,
    },
    error: {
      bg: "transparent",
      color: theme.text.primary,
      borderColor: theme.borderGlass.default,
    },
    warning: {
      bg: theme.accent.mutedBg,
      color: theme.accent.muted,
      borderColor: theme.accent.mutedBorder,
    },
    info: {
      bg: interactive.neutral.bg,
      color: theme.text.secondary,
      borderColor: theme.borderGlass.default,
    },
  };

  const colors = statusColors[status];

  return (
    <ChakraAlert
      borderRadius="8px"
      borderWidth="1px"
      borderColor={colors.borderColor}
      bg={colors.bg}
      color={colors.color}
      {...props}
    >
      <AlertIcon color={status === "error" ? theme.text.tertiary : colors.color} />
      {children}
    </ChakraAlert>
  );
}

export { AlertIcon, AlertTitle, AlertDescription };
